import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { buildCampusNavHref } from "../../utils/studentNavPaths";
import { getNodeAtPath, pathKeyFromIndices, titlesAlongPath } from "../../utils/temarioTree";
import { findCourseFileInTabs, selectAccordionRowForFileLocation } from "../../utils/findCourseFileLocation";
import { normalizeTabKey, testMatchesTab } from "../../utils/testMatchesTab";
import { getMessage } from "../../data/locales";
import { API_URL } from "../../config";
import { getCourseFileObjectKey } from "../../utils/signedCourseFileUrl";
import { displayArchivoNombre } from "../../utils/archivoDisplayName";
import { buildTestLaunchHref } from "../../utils/buildTestLaunchHref";
import { collectArchivosFromNode, collectTestRefsFromNode } from "../../utils/topicTemarioCollectors";
import { temarioNodeHasVisibleContent } from "../../utils/campusVisibleContent";
import { ensureExpandedKey, toggleExpandedKey } from "../../utils/toggleExpandedKey";
import { listContenidoItems } from "../../utils/contenidoOrden";
import { courseTestTitle } from "../../utils/listLinkableCourseTests";
import { buildAccordionResourceRows } from "../../utils/buildAccordionResourceRows";
import { listCampusAccordionSections } from "../../utils/listCampusAccordionSections";
import { applyCatalogTestsToTabTree, resolveCourseTestsCatalog } from "../../utils/filterTemarioTestsForTab";
import { getContenidoNodesForTab } from "../../utils/normalizeCourseShape";
import { scrollStudentContentToTop } from "../../utils/scrollStudentContentToTop";
import CourseMaterialInlineViewer from "../CourseMaterialInlineViewer";
import CoursePestanasNav from "../CoursePestanasNav";
import TopicTemarioNodeCard, { TopicTemarioNestedNode } from "./TopicTemarioNode";
import TopicViewerAccordion from "./TopicViewerAccordion";
import CampusTestOverlay from "./CampusTestOverlay";
import "./TopicViewer.css";
import "../../pages/TestTemas.css";

function expandAccordionSourceKeys(currentKeys, tab, sourceIndex) {
  const prefix = `${tab}:`;
  return ["folder", "files", "tests"].reduce(
    (keys, kind) => ensureExpandedKey(keys, `${prefix}${sourceIndex}-${kind}`),
    currentKeys,
  );
}

const FOCUSABLE_SELECTOR = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";
const EMPTY_FOCUS_NAV_PATH = [];

function buildUnavailableArchivoEmbed(archivo) {
  // Signals CourseMaterialEmbedModal to show the standard file-open failure message.
  return {
    ...(archivo && typeof archivo === "object" ? archivo : {}),
    nombre: getMessage("cursoUsuario.fileOpenFailed"),
    storageKey: "",
  };
}

const ALWAYS_ALLOWED_DEMO_VIDEO_HOSTS = new Set(["interactive-examples.mdn.mozilla.net"]);
// archive.org demo videos are only useful for local testing; disallow them elsewhere.
const LOCAL_ONLY_DEMO_VIDEO_HOSTS = new Set(["archive.org"]);
const IS_LOCAL_ENVIRONMENT = API_URL.toLowerCase().includes("local");

function isAllowedDemoVideoUrl(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") {
      return false;
    }
    if (ALWAYS_ALLOWED_DEMO_VIDEO_HOSTS.has(url.hostname)) {
      return true;
    }
    return IS_LOCAL_ENVIRONMENT && LOCAL_ONLY_DEMO_VIDEO_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

const getFileIcon = (fileName = "") => {
  const name = String(fileName).toLowerCase();
  if (name.endsWith(".pdf")) return "📄";
  if (name.endsWith(".mp4") || name.endsWith(".mov")) return "🎥";
  return "📁";
};

const getArchivoLoadingKey = (archivo) =>
  archivo?.url || archivo?.nombre || "__resource__";

function getPreguntaText(pregunta, index) {
  if (typeof pregunta === "string") {
    return pregunta;
  }
  if (!pregunta || typeof pregunta !== "object") {
    return `Pregunta ${index + 1}`;
  }
  return pregunta.pregunta || pregunta.enunciado || `Pregunta ${index + 1}`;
}

function getPreguntaAnswers(pregunta) {
  if (!pregunta || typeof pregunta !== "object") {
    return [];
  }
  if (Array.isArray(pregunta.respuestas)) {
    return pregunta.respuestas;
  }
  if (Array.isArray(pregunta.opciones)) {
    return pregunta.opciones;
  }
  return [];
}

function getCampusHierarchyLevelLabel(depth) {
  if (depth <= 0) return getMessage("topic.hierarchySections");
  if (depth === 1) return getMessage("topic.hierarchyUnits");
  if (depth === 2) return getMessage("topic.hierarchyTopics");
  return getMessage("topic.hierarchyContent");
}

function getVisibleCampusHierarchy({ selectedNode, selectedChildren, focusNavPath, temas }) {
  if (!selectedNode) {
    return {
      visibleNodes: temas,
      visibleBasePath: [],
      isShowingSelectedContent: false,
    };
  }
  if (selectedChildren.length > 0) {
    return {
      visibleNodes: selectedChildren,
      visibleBasePath: focusNavPath,
      isShowingSelectedContent: false,
    };
  }
  return {
    visibleNodes: [selectedNode],
    visibleBasePath: [],
    isShowingSelectedContent: true,
  };
}

function isCatalogTestBlock(node) {
  // Catalog fillers are the tab's own test list. Only tests linked on a content block
  // should leave that list.
  return typeof node?.id === "string" && node.id.startsWith("test-block-");
}

function collectListedTestTitles(nodes, titles) {
  const found = titles instanceof Set ? titles : new Set();
  if (!Array.isArray(nodes)) {
    return found;
  }
  nodes.forEach((node) => {
    if (!node || typeof node !== "object" || isCatalogTestBlock(node)) {
      return;
    }
    listContenidoItems(node).forEach((item) => {
      if (item.kind !== "test") {
        return;
      }
      const title = typeof item.testRef?.titulo === "string" ? item.testRef.titulo.trim() : "";
      if (title) {
        found.add(title);
      }
    });
    collectListedTestTitles(node.hijos, found);
  });
  return found;
}

function PreviewTabTests({ tests, pestanaActiva, onOpenTest, showCourseFallback = false }) {
  if (!Array.isArray(tests) || tests.length === 0) {
    return null;
  }

  return (
    <section className="topic-preview-tests" aria-labelledby="topic-preview-tests-title">
      <h3 id="topic-preview-tests-title">
        {showCourseFallback ? "Tests del curso" : "Tests de esta pestaña"}
      </h3>
      <div className="topic-preview-tests-grid">
        {tests.map((test, index) => (
          <article className="topic-preview-test-card" key={`${pestanaActiva}-${test.titulo || "test"}-${index}`}>
            <h4>{test.titulo || "Test"}</h4>
            <p>
              {Array.isArray(test.preguntas) ? test.preguntas.length : 0} preguntas
              {typeof test.tiempo === "number" && test.tiempo > 0 ? ` · ${test.tiempo} min` : ""}
            </p>
            <button type="button" className="btn-access" onClick={() => onOpenTest(test)}>
              Abrir test
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

PreviewTabTests.propTypes = {
  tests: PropTypes.arrayOf(PropTypes.object).isRequired,
  pestanaActiva: PropTypes.string.isRequired,
  onOpenTest: PropTypes.func.isRequired,
  showCourseFallback: PropTypes.bool,
};

const TopicViewer = ({
  curso,
  initialPestana = "",
  previewMode = false,
  activePestana: controlledPestana = "",
  onPestanaChange = null,
  focusNavPath = EMPTY_FOCUS_NAV_PATH,
  focusArchivo = "",
}) => {
  const navigate = useNavigate();
  const [internalPestana, setInternalPestana] = useState("");
  const isControlledPreview = previewMode && typeof onPestanaChange === "function";
  const pestanasCurso = useMemo(
    () => (Array.isArray(curso?.pestanasCurso) ? curso.pestanasCurso : []),
    [curso?.pestanasCurso],
  );
  const initialPestanaLimpia = typeof initialPestana === "string" ? initialPestana.trim() : "";
  const pestanaUrlValida = initialPestanaLimpia && pestanasCurso.includes(initialPestanaLimpia)
    ? initialPestanaLimpia
    : "";
  const pestanaInternaValida = internalPestana && pestanasCurso.includes(internalPestana)
    ? internalPestana
    : "";
  const pestanaActiva = isControlledPreview
    ? controlledPestana
    : pestanaUrlValida || pestanaInternaValida || pestanasCurso[0] || "";
  const setPestanaActiva = isControlledPreview ? onPestanaChange : setInternalPestana;
  const [temaModal, setTemaModal] = useState(null);
  const [previewTestModal, setPreviewTestModal] = useState(null);
  const [inlineArchivo, setInlineArchivo] = useState(null);
  const [loadingArchivo, setLoadingArchivo] = useState(null);
  const [previewNavPath, setPreviewNavPath] = useState([]);
  const [expandedAccordionKeys, setExpandedAccordionKeys] = useState([]);
  const [activeInlinePanel, setActiveInlinePanel] = useState(null);
  const [campusTestHref, setCampusTestHref] = useState(null);
  const modalRef = useRef(null);
  const previousActiveRef = useRef(null);
  const loadingArchivoRef = useRef(null);
  const markedContentRef = useRef(new Set());

  useEffect(() => {
    if (!curso?.pestanasCurso?.length) return;
    if (isControlledPreview) return;
    setInternalPestana((prev) => (prev === pestanaActiva ? prev : pestanaActiva));
  }, [curso?.pestanasCurso, isControlledPreview, pestanaActiva]);

  useEffect(() => {
    if (previewMode) {
      return;
    }
    // Keep the campus course header visible; deep-link focus must not scroll it away.
    const frame = requestAnimationFrame(() => {
      scrollStudentContentToTop();
    });
    return () => cancelAnimationFrame(frame);
  }, [focusNavPath, focusArchivo, pestanaActiva, previewMode]);

  useEffect(() => {
    setPreviewNavPath([]);
    setActiveInlinePanel(null);
    setInlineArchivo(null);
    setCampusTestHref(null);
  }, [pestanaActiva]);

  useEffect(() => {
    if (!temaModal) return;
    previousActiveRef.current = document.activeElement;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setTemaModal(null);
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusables = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    const raf = requestAnimationFrame(() => {
      const first = modalRef.current?.querySelector(FOCUSABLE_SELECTOR);
      (first || modalRef.current)?.focus();
    });
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(raf);
      previousActiveRef.current?.focus?.();
    };
  }, [temaModal]);

  const markContentViewed = useCallback((payload) => {
    const courseId = curso?.id || curso?._id;
    if (previewMode || !courseId || !payload?.pestana || !payload?.pathKey) {
      return;
    }
    const tipo = payload.tipo === "archivo" ? "archivo" : "apartado";
    const markerKey = [
      courseId,
      tipo,
      payload.pestana,
      payload.pathKey,
      payload.archivoNombre || "",
      payload.archivoIndex ?? "",
    ].join(":");
    if (markedContentRef.current.has(markerKey)) {
      return;
    }
    markedContentRef.current.add(markerKey);
    fetch(`${API_URL}/api/cursos/${courseId}/contenido-visto`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        tipo,
        pestana: payload.pestana,
        pathKey: payload.pathKey,
        archivoNombre: payload.archivoNombre || "",
        archivoIndex: payload.archivoIndex,
        titulo: payload.titulo || "",
        ruta: payload.ruta || "",
      }),
    }).catch(() => {
      markedContentRef.current.delete(markerKey);
    });
  }, [curso?.id, curso?._id, previewMode]);

  const openResource = useCallback((archivo, contentMeta = null) => {
    if (loadingArchivoRef.current !== null) return;
    if (contentMeta) {
      const progressTab = typeof contentMeta.pestana === "string" && contentMeta.pestana.trim()
        ? contentMeta.pestana.trim()
        : pestanaActiva;
      markContentViewed({
        tipo: "archivo",
        pestana: progressTab,
        pathKey: contentMeta.pathKey,
        archivoNombre: archivo?.nombre || "",
        archivoIndex: contentMeta.archivoIndex,
        titulo: archivo?.nombre || "",
        ruta: [contentMeta.parentTitle, archivo?.nombre].filter(Boolean).join(" / "),
      });
    }
    const loadingKey = getArchivoLoadingKey(archivo);
    loadingArchivoRef.current = loadingKey;
    setLoadingArchivo(loadingKey);

    try {
      const objectKey = getCourseFileObjectKey(archivo);
      const directUrl = typeof archivo?.url === "string" ? archivo.url.trim() : "";
      if (!objectKey && !isAllowedDemoVideoUrl(directUrl)) {
        setInlineArchivo(buildUnavailableArchivoEmbed(archivo));
        return;
      }
      setInlineArchivo(archivo);
    } catch (error) {
      console.error("Error opening course file:", error);
      setInlineArchivo(buildUnavailableArchivoEmbed(archivo));
    } finally {
      loadingArchivoRef.current = null;
      setLoadingArchivo(null);
    }
  }, [markContentViewed, pestanaActiva]);

  const courseTests = useMemo(
    () => resolveCourseTestsCatalog(curso?.tests, curso?.testEstructura),
    [curso?.testEstructura, curso?.tests],
  );
  const matchingPreviewTests = useMemo(
    () => (previewMode ? courseTests.filter((test) => testMatchesTab(test, pestanaActiva)) : []),
    [courseTests, pestanaActiva, previewMode],
  );
  const showCourseTestFallback = previewMode && matchingPreviewTests.length === 0 && courseTests.length > 0;

  const temas = useMemo(
    () => applyCatalogTestsToTabTree(
      getContenidoNodesForTab(curso?.contenido, pestanaActiva),
      courseTests,
      pestanaActiva,
      pestanasCurso,
    ),
    [courseTests, curso?.contenido, pestanaActiva, pestanasCurso],
  );
  const listedTestTitles = useMemo(() => collectListedTestTitles(temas), [temas]);
  const activePreviewTests = useMemo(
    () => {
      if (!previewMode) {
        return [];
      }
      const source = showCourseTestFallback ? courseTests : matchingPreviewTests;
      return source.filter((test) => !listedTestTitles.has(courseTestTitle(test)));
    },
    [courseTests, listedTestTitles, matchingPreviewTests, previewMode, showCourseTestFallback],
  );
  const accordionSections = useMemo(
    () => listCampusAccordionSections(
      curso?.contenido,
      pestanasCurso,
      previewMode || initialPestanaLimpia ? pestanaActiva : "",
      courseTests,
    ),
    [courseTests, curso?.contenido, initialPestanaLimpia, pestanaActiva, pestanasCurso, previewMode],
  );
  const accordionRows = useMemo(
    () => accordionSections.flatMap((section) => (
      Array.isArray(section.blocks) ? section.blocks : []
    ).flatMap((block, blockIndex) => (
      buildAccordionResourceRows(
        block,
        Number.isInteger(block?.sourceIndex) ? block.sourceIndex : blockIndex,
      ).map((row) => ({
        ...row,
        rowKey: `${section.tab}:${row.rowKey}`,
        tab: section.tab,
      }))
    ))),
    [accordionSections],
  );
  const externalFocusNavPath = useMemo(
    () => (Array.isArray(focusNavPath) ? focusNavPath : EMPTY_FOCUS_NAV_PATH),
    [focusNavPath],
  );
  const safeFocusNavPath = previewMode ? previewNavPath : externalFocusNavPath;

  useEffect(() => {
    if (previewMode) {
      return;
    }
    if (externalFocusNavPath.length > 0) {
      setExpandedAccordionKeys((current) =>
        expandAccordionSourceKeys(current, pestanaActiva, externalFocusNavPath[0]),
      );
    }
  }, [externalFocusNavPath, pestanaActiva, previewMode]);
  const selectedNode = safeFocusNavPath.length > 0
    ? getNodeAtPath(temas, safeFocusNavPath)
    : null;
  const selectedChildren = Array.isArray(selectedNode?.hijos) ? selectedNode.hijos : [];
  const {
    visibleNodes,
    visibleBasePath,
    isShowingSelectedContent,
  } = getVisibleCampusHierarchy({ selectedNode, selectedChildren, focusNavPath: safeFocusNavPath, temas });
  const hierarchyTrail = titlesAlongPath(temas, safeFocusNavPath);
  const visibleLevelDepth = isShowingSelectedContent ? safeFocusNavPath.length : visibleBasePath.length;
  const visibleLevelLabel = isShowingSelectedContent
    ? getMessage("topic.hierarchyContent")
    : getCampusHierarchyLevelLabel(visibleLevelDepth);

  const isBlockCompleted = (blockId) => {
    if (!curso?.userProgress || !Array.isArray(curso.userProgress)) return false;
    const entry = curso.userProgress.find((p) => p[blockId] !== undefined);
    return entry?.[blockId] === true;
  };

  const completedCount = accordionSections.reduce(
    (total, section) => total + (Array.isArray(section.blocks) ? section.blocks : []).filter((block) => block?.id && isBlockCompleted(block.id)).length,
    0,
  );
  const totalBlocks = accordionSections.reduce(
    (total, section) => total + (Array.isArray(section.blocks) ? section.blocks.length : 0),
    0,
  );
  const testResults = curso?.testResults || [];
  const testsPassed = testResults.filter((r) => r.passed).length;
  const testsCompleted = testResults.length;

  const cursoId = curso?.id || curso?._id;

  useEffect(() => {
    if (!selectedNode || safeFocusNavPath.length === 0) {
      return;
    }
    markContentViewed({
      tipo: "apartado",
      pestana: pestanaActiva,
      pathKey: pathKeyFromIndices(safeFocusNavPath),
      titulo: selectedNode.nombre || "",
      ruta: titlesAlongPath(temas, safeFocusNavPath).join(" / "),
    });
  }, [markContentViewed, pestanaActiva, safeFocusNavPath, selectedNode, temas]);

  const findPreviewTest = (testRef) => {
    const title = typeof testRef?.titulo === "string" ? testRef.titulo.trim() : "";
    if (!title) {
      return null;
    }
    const normalizedTitle = normalizeTabKey(title);
    const normalizedBlock = normalizeTabKey(testRef?.bloque || pestanaActiva);
    return courseTests.find((test) => {
      if (normalizeTabKey(test?.titulo || "") !== normalizedTitle) {
        return false;
      }
      if (!normalizedBlock) {
        return true;
      }
      return testMatchesTab(test, normalizedBlock) || normalizeTabKey(test?.bloque || "") === normalizedBlock;
    }) || courseTests.find((test) => normalizeTabKey(test?.titulo || "") === normalizedTitle) || null;
  };

  const openPreviewTest = (test) => {
    if (!test || typeof test !== "object") {
      return;
    }
    setPreviewTestModal({
      ref: { titulo: test.titulo || "Test", bloque: test.bloque || pestanaActiva },
      test,
    });
  };

  const handleOpenTestRef = (testRef, tabLabel = pestanaActiva) => {
    if (previewMode) {
      setPreviewTestModal({
        ref: testRef,
        test: findPreviewTest(testRef),
      });
      return;
    }
    // Stay on Campus: mount TestTemas in an overlay (browser URL unchanged).
    setCampusTestHref(buildTestLaunchHref(testRef, tabLabel));
  };

  const handleCloseCampusTest = useCallback(() => {
    setCampusTestHref(null);
  }, []);

  const handleVerContenido = (tema, navPath = []) => {
    const pathKey = pathKeyFromIndices(navPath);
    markContentViewed({
      tipo: "apartado",
      pestana: pestanaActiva,
      pathKey,
      titulo: tema?.nombre || "",
      ruta: titlesAlongPath(temas, navPath).join(" / ") || tema?.nombre || "",
    });
    const ownItems = listContenidoItems(tema);
    const hasOwnFiles = ownItems.some((item) => item.kind === "archivo");
    if (hasOwnFiles) {
      setInlineArchivo(null);
      setTemaModal({ ...tema, __navPath: navPath, __pathKey: pathKey });
      return;
    }
    const ownTest = ownItems.find((item) => item.kind === "test");
    if (ownTest) {
      handleOpenTestRef(ownTest.testRef);
      return;
    }
    const archivos = collectArchivosFromNode(tema);
    if (archivos.length > 0) {
      setInlineArchivo(null);
      setTemaModal({ ...tema, archivos, testRefs: [], contenidoOrden: [], __navPath: navPath, __pathKey: pathKey });
      return;
    }
    const refs = collectTestRefsFromNode(tema);
    if (refs.length > 0) {
      handleOpenTestRef(refs[0]);
      return;
    }
    setInlineArchivo(null);
    setTemaModal({
      ...tema,
      archivos: [],
      __navPath: navPath,
      __pathKey: pathKey,
      __isEmpty: true
    });
  };

  const handleVerCursoCompleto = () => {
    setTemaModal(null);
    setInlineArchivo(null);
    if (!previewMode) {
      navigate("/usuario/campus");
    }
  };

  const handleCloseTemaModal = () => {
    setTemaModal(null);
    setInlineArchivo(null);
  };

  const handleClosePreviewTest = () => {
    setPreviewTestModal(null);
  };

  const handleNavigateNode = (navPath) => {
    const node = getNodeAtPath(temas, navPath);
    if (node) {
      markContentViewed({
        tipo: "apartado",
        pestana: pestanaActiva,
        pathKey: pathKeyFromIndices(navPath),
        titulo: node.nombre || "",
        ruta: titlesAlongPath(temas, navPath).join(" / "),
      });
    }
    if (previewMode) {
      setPreviewNavPath(Array.isArray(navPath) ? navPath : []);
      return;
    }
    navigate(buildCampusNavHref(pestanaActiva, navPath));
  };

  const handleActivateAccordionRow = (row, tabLabel = pestanaActiva) => {
    const node = row?.node && typeof row.node === "object" ? row.node : {};
    const navPath = Array.isArray(row?.navPath) ? row.navPath : [];
    const rowKey = typeof row?.rowKey === "string" ? row.rowKey : "";
    const rowTab = typeof tabLabel === "string" && tabLabel.trim() ? tabLabel.trim() : pestanaActiva;
    if (previewMode) {
      handleVerContenido(node, navPath);
      return;
    }

    if (activeInlinePanel?.rowKey === rowKey && !campusTestHref) {
      setActiveInlinePanel(null);
      setInlineArchivo(null);
      return;
    }

    if (rowTab && rowTab !== pestanaActiva) {
      setInternalPestana(rowTab);
    }

    const tabBlocks = getContenidoNodesForTab(curso?.contenido, rowTab);
    const pathKey = pathKeyFromIndices(navPath);
    markContentViewed({
      tipo: "apartado",
      pestana: rowTab,
      pathKey,
      titulo: node?.nombre || "",
      ruta: titlesAlongPath(tabBlocks, navPath).join(" / ") || node?.nombre || "",
    });

    const ownArchivos = Array.isArray(node?.archivos) ? node.archivos : [];
    const ownTestRefs = Array.isArray(node?.testRefs) ? node.testRefs : [];
    const hasNestedChildren = Array.isArray(node?.hijos)
      && node.hijos.some((child) => temarioNodeHasVisibleContent(child));
    const archivos = row?.kind === "archivo" && row.archivo
      ? [row.archivo]
      : (ownArchivos.length > 0 || hasNestedChildren ? ownArchivos : collectArchivosFromNode(node));
    const testRefs = row?.kind === "testRef" && row.testRef
      ? [row.testRef]
      : (ownTestRefs.length > 0 || hasNestedChildren ? ownTestRefs : collectTestRefsFromNode(node));

    if (archivos.length === 0 && testRefs.length > 0 && !hasNestedChildren) {
      setInlineArchivo(null);
      setActiveInlinePanel({
        rowKey,
        navPath,
        node,
        archivos: [],
        testRefs,
        pathKey,
        kind: "test",
        tab: rowTab,
      });
      handleOpenTestRef(testRefs[0], rowTab);
      return;
    }

    setCampusTestHref(null);
    setActiveInlinePanel({
      rowKey,
      navPath,
      node,
      archivos,
      testRefs,
      pathKey,
      archivoIndex: row?.archivoIndex,
      kind: "files",
      tab: rowTab,
    });
    setInlineArchivo(null);
    if (archivos.length === 1) {
      void openResource(archivos[0], {
        pathKey,
        parentTitle: node?.nombre || "",
        archivoIndex: row?.archivoIndex ?? 0,
      });
    }
  };

  useEffect(() => {
    const requestedKey = typeof focusArchivo === "string" ? focusArchivo.trim() : "";
    if (previewMode || !requestedKey || accordionRows.length === 0) {
      return;
    }

    const tabIsFixed = initialPestanaLimpia.length > 0;
    const searchedTabs = tabIsFixed
      ? [{ tab: pestanaActiva, nodes: temas }]
      : accordionSections.map((section) => ({
        tab: section.tab,
        nodes: getContenidoNodesForTab(curso?.contenido, section.tab),
      }));
    const match = findCourseFileInTabs(searchedTabs, requestedKey, safeFocusNavPath);
    const location = match?.location ?? null;
    const progressTab = match?.tab || pestanaActiva;
    const row = selectAccordionRowForFileLocation(
      tabIsFixed ? accordionRows : accordionRows.filter((item) => item.tab === progressTab),
      location,
    );
    if (!location || !row) {
      return;
    }

    const navPath = Array.isArray(row.navPath) ? row.navPath : [];
    const files = row?.kind === "archivo" && row.archivo
      ? [row.archivo]
      : collectArchivosFromNode(row?.node);
    setExpandedAccordionKeys((current) =>
      expandAccordionSourceKeys(current, row.tab || progressTab, navPath[0] ?? 0),
    );
    setCampusTestHref(null);
    setActiveInlinePanel({
      rowKey: row.rowKey,
      navPath,
      node: row.node,
      archivos: files,
      testRefs: collectTestRefsFromNode(row.node),
      pathKey: pathKeyFromIndices(navPath),
      archivoIndex: row.archivoIndex,
      kind: "files",
      tab: row.tab || progressTab,
    });
    void openResource(location.archivo, {
      pathKey: pathKeyFromIndices(location.path),
      parentTitle: location.node?.nombre || "",
      archivoIndex: location.archivoIndex,
      pestana: progressTab,
    });
  }, [
    accordionRows,
    accordionSections,
    curso?.contenido,
    focusArchivo,
    initialPestanaLimpia,
    openResource,
    pestanaActiva,
    previewMode,
    safeFocusNavPath,
    temas,
  ]);

  const renderAccordionRowPanel = (row) => {
    if (!activeInlinePanel || activeInlinePanel.rowKey !== row.rowKey) {
      return null;
    }
    if (activeInlinePanel.kind === "test") {
      return (
        <div className="topic-inline-panel" aria-live="polite">
          <p className="topic-inline-panel-hint">{getMessage("topic.inlineTestReady")}</p>
        </div>
      );
    }
    const archivos = Array.isArray(activeInlinePanel.archivos) ? activeInlinePanel.archivos : [];
    const testRefs = Array.isArray(activeInlinePanel.testRefs) ? activeInlinePanel.testRefs : [];
    const mixedItems = listContenidoItems(
      activeInlinePanel.node || { archivos, testRefs },
    );
    return (
      <div className="topic-inline-panel">
        {mixedItems.length === 0 &&
        (!Array.isArray(activeInlinePanel.node?.hijos) || activeInlinePanel.node.hijos.length === 0) ? (
          <p className="topic-inline-panel-hint">{getMessage("topic.noContentInSection")}</p>
        ) : (
          <ul className="topic-inline-file-list">
            {mixedItems.map((item) => {
              if (item.kind === "test") {
                const testRef = item.testRef;
                return (
                  <li key={`test-${testRef?.bloque || pestanaActiva}-${testRef?.titulo || item.sourceIndex}`}>
                    <span className="topic-modal-file-name">{testRef?.titulo || "Test"}</span>
                    <button
                      type="button"
                      className="topic-modal-file-btn"
                      onClick={() => handleOpenTestRef(testRef, activeInlinePanel.tab || pestanaActiva)}
                    >
                      {getMessage("topic.openTest")}
                    </button>
                  </li>
                );
              }
              const archivo = item.archivo;
              return (
                <li key={getArchivoLoadingKey(archivo)}>
                  <button
                    type="button"
                    className="topic-modal-file-btn"
                    onClick={() => {
                      void openResource(archivo, {
                        pathKey: activeInlinePanel.pathKey,
                        parentTitle: activeInlinePanel.node?.nombre || "",
                        archivoIndex: activeInlinePanel.archivoIndex ?? item.sourceIndex,
                        pestana: activeInlinePanel.tab || pestanaActiva,
                      });
                    }}
                    disabled={loadingArchivo !== null}
                  >
                    <span>
                      {loadingArchivo === getArchivoLoadingKey(archivo) ? "⏳" : getFileIcon(archivo.nombre)}
                    </span>
                    <span className="topic-modal-file-name" title={displayArchivoNombre(archivo)}>
                      {loadingArchivo === getArchivoLoadingKey(archivo)
                        ? getMessage("topic.loading")
                        : displayArchivoNombre(archivo)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {Array.isArray(activeInlinePanel.node?.hijos) && activeInlinePanel.node.hijos.length > 0 ? (
          <div className="topic-temario-nested-children">
            {activeInlinePanel.node.hijos.map((child, childIndex) => (
              <TopicTemarioNestedNode
                key={`${activeInlinePanel.rowKey}-child-${childIndex}`}
                node={child}
                depth={1}
                navPathPrefix={[...activeInlinePanel.navPath, childIndex]}
                focusNavPath={focusNavPath}
                pestanaActiva={pestanaActiva}
                openResource={(archivo, archivoIndex, navPath, node) => void openResource(archivo, {
                  pathKey: pathKeyFromIndices(navPath),
                  parentTitle: node?.nombre || "",
                  archivoIndex,
                  pestana: activeInlinePanel.tab || pestanaActiva,
                })}
                loadingArchivo={loadingArchivo}
                getArchivoLoadingKey={getArchivoLoadingKey}
                navigate={navigate}
                onTestRefOpen={handleOpenTestRef}
              />
            ))}
          </div>
        ) : null}
        {inlineArchivo ? (
          <div className="topic-inline-viewer" aria-label="Vista integrada del archivo">
            <CourseMaterialInlineViewer
              archivo={inlineArchivo}
              canDownload={curso?.contenidoDescargable === true}
            />
          </div>
        ) : null}
      </div>
    );
  };

  if (!curso) return null;

  return (
    <div className={`topic-viewer-container${previewMode ? "" : " topic-viewer-container--accordion cds-test-root dark-theme"}`}>
      {previewMode ? (
        <CoursePestanasNav
          previewMode={previewMode}
          activePestana={pestanaActiva}
          onSelectPestana={setPestanaActiva}
        />
      ) : null}

      {(totalBlocks > 0 || testsCompleted > 0) && (
        <div className="topic-viewer-summary">
          {totalBlocks > 0 && (
            <span className="topic-blocks-count">
              {getMessage("blocks.completedCount", { completed: completedCount, total: totalBlocks })}
            </span>
          )}
          {testsCompleted > 0 && (
            <>
              <span className="topic-tests-badges">
                <span className="topic-badge topic-badge-passed">
                  {getMessage("tests.passedCount", { n: testsPassed })}
                </span>
                <span className="topic-badge topic-badge-completed">
                  {getMessage("tests.completedCount", { n: testsCompleted })}
                </span>
              </span>
              <div className="topic-test-list">
                {testResults.slice(0, 8).map((tr, i) => (
                  <span
                    key={tr?.testId != null ? String(tr.testId) : `test-result-${i}`}
                    className={`topic-test-item ${tr.passed ? "passed" : "completed"}`}
                    title={tr.tituloTest}
                  >
                    {tr.tituloTest || getMessage("tests.completed")} · {tr.passed ? getMessage("tests.passed") : getMessage("tests.completed")}
                  </span>
                ))}
                {testResults.length > 8 && (
                  <span className="topic-test-list-more">
                    {getMessage("tests.moreCount", { n: testResults.length - 8 })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {previewMode ? (
        <>
          <div className="topic-hierarchy-heading">
            <span className="topic-hierarchy-level">{visibleLevelLabel}</span>
            {hierarchyTrail.length > 0 ? (
              <span className="topic-hierarchy-path">{hierarchyTrail.join(" / ")}</span>
            ) : (
              <span className="topic-hierarchy-path">{pestanaActiva}</span>
            )}
          </div>
          <div className="topics-grid">
            {visibleNodes.length > 0 ? (
              visibleNodes.map((tema, idx) => {
                const navPathPrefix = isShowingSelectedContent
                  ? safeFocusNavPath
                  : [...visibleBasePath, idx];
                return (
                  <TopicTemarioNodeCard
                    key={tema.id ?? idx}
                    node={tema}
                    nodeIndex={idx}
                    navPathPrefix={navPathPrefix}
                    focusNavPath={safeFocusNavPath}
                    pestanaActiva={pestanaActiva}
                    openResource={openResource}
                    loadingArchivo={loadingArchivo}
                    getArchivoLoadingKey={getArchivoLoadingKey}
                    isBlockCompleted={isBlockCompleted}
                    onVerContenido={handleVerContenido}
                    onNavigateNode={handleNavigateNode}
                    cursoId={cursoId}
                  />
                );
              })
            ) : (
              <div className="empty-state">
                <p>No hay temas disponibles en esta sección.</p>
              </div>
            )}
          </div>
        </>
      ) : accordionSections.length === 0 ? (
        <div className="empty-state">
          <p>No hay temas disponibles en esta sección.</p>
        </div>
      ) : (
        accordionSections.map((section) => (
          <section key={section.tab} className="topic-home-tab-section">
            <h3 className="topic-home-tab-heading">{section.tab}</h3>
            <TopicViewerAccordion
              blocks={section.blocks}
              keyPrefix={`${section.tab}:`}
              expandedKeys={expandedAccordionKeys}
              onToggle={(key) => {
                setExpandedAccordionKeys((current) => toggleExpandedKey(current, key));
              }}
              focusNavPath={safeFocusNavPath}
              isBlockCompleted={isBlockCompleted}
              onActivateRow={(row) => handleActivateAccordionRow(row, section.tab)}
              activeRowKey={activeInlinePanel?.rowKey || null}
              renderRowPanel={renderAccordionRowPanel}
            />
          </section>
        ))
      )}

      <PreviewTabTests
        tests={activePreviewTests}
        pestanaActiva={pestanaActiva}
        onOpenTest={openPreviewTest}
        showCourseFallback={showCourseTestFallback}
      />

      {temaModal &&
        createPortal(
          <div
            className="topic-modal-backdrop"
            role="presentation"
            onClick={handleCloseTemaModal}
          >
            <div
              ref={modalRef}
              className={`topic-modal-content topic-file-modal${inlineArchivo ? " topic-file-modal--with-viewer" : ""}`}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-labelledby="topic-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="topic-modal-header">
                <h3 id="topic-modal-title">{getMessage("topic.selectContent")}</h3>
                <button
                  type="button"
                  className="topic-modal-close"
                  onClick={handleCloseTemaModal}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
              <div className="topic-file-modal-body">
                {temaModal.__isEmpty ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    <p style={{ margin: '0 0 12px', fontSize: '1.1rem' }}>📂 Este apartado no tiene contenido disponible</p>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>El instructor aún no ha subido archivos ni tests para esta sección.</p>
                  </div>
                ) : (
                  <ul className="topic-modal-list">
                    {listContenidoItems(temaModal).map((item) => {
                      if (item.kind === "test") {
                        const testRef = item.testRef;
                        const title = testRef?.titulo || "Test";
                        return (
                          <li key={`test-${testRef?.bloque || pestanaActiva}-${title}-${item.sourceIndex}`}>
                            <span className="topic-modal-file-name">{title}</span>
                            <button
                              type="button"
                              className="topic-modal-file-btn"
                              onClick={() => handleOpenTestRef(testRef, pestanaActiva)}
                            >
                              {getMessage("topic.openTest")}
                            </button>
                          </li>
                        );
                      }
                      const archivo = item.archivo;
                      return (
                        <li key={getArchivoLoadingKey(archivo)}>
                          <button
                            type="button"
                            className="topic-modal-file-btn"
                            onClick={async () => {
                              await openResource(archivo, {
                                pathKey: temaModal.__pathKey,
                                parentTitle: temaModal.nombre,
                                archivoIndex: item.sourceIndex,
                              });
                            }}
                            disabled={loadingArchivo !== null}
                          >
                            <span>{loadingArchivo === getArchivoLoadingKey(archivo) ? "⏳" : getFileIcon(archivo.nombre)}</span>
                            <span
                              className="topic-modal-file-name"
                              title={displayArchivoNombre(archivo)}
                            >
                              {loadingArchivo === getArchivoLoadingKey(archivo) ? getMessage("topic.loading") : displayArchivoNombre(archivo)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {inlineArchivo ? (
                  <div className="topic-modal-inline-viewer" aria-label="Vista integrada del archivo">
                    <CourseMaterialInlineViewer
                      archivo={inlineArchivo}
                      canDownload={curso?.contenidoDescargable === true}
                    />
                  </div>
                ) : null}
              </div>
              <div className="topic-modal-footer">
                <button
                  type="button"
                  className="topic-modal-ver-curso"
                  onClick={handleVerCursoCompleto}
                >
                  Ver curso completo →
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {campusTestHref ? (
        <CampusTestOverlay launchHref={campusTestHref} onClose={handleCloseCampusTest} />
      ) : null}

      {previewTestModal &&
        createPortal(
          <div
            className="topic-modal-backdrop"
            role="presentation"
            onClick={handleClosePreviewTest}
          >
            <div
              className="topic-modal-content topic-test-preview-modal"
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-labelledby="topic-test-preview-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="topic-modal-header">
                <h3 id="topic-test-preview-title">
                  Vista previa del test: {previewTestModal.ref?.titulo || "Test"}
                </h3>
                <button
                  type="button"
                  className="topic-modal-close"
                  onClick={handleClosePreviewTest}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>
              <div className="topic-test-preview-body">
                {previewTestModal.test ? (
                  <>
                    <p className="topic-test-preview-meta">
                      Bloque: {previewTestModal.test.bloque || previewTestModal.ref?.bloque || "Sin bloque"}
                    </p>
                    {Array.isArray(previewTestModal.test.preguntas) && previewTestModal.test.preguntas.length > 0 ? (
                      <ol className="topic-test-preview-questions">
                        {previewTestModal.test.preguntas.map((pregunta, index) => (
                          <li key={`${previewTestModal.test.titulo || "test"}-${index}`}>
                            <p className="topic-test-preview-question-text">
                              {getPreguntaText(pregunta, index)}
                            </p>
                            {pregunta?.imagenUrl ? (
                              <img
                                className="topic-test-preview-question-image"
                                src={pregunta.imagenUrl}
                                alt={`Imagen de la pregunta ${index + 1}`}
                              />
                            ) : null}
                            {getPreguntaAnswers(pregunta).length > 0 ? (
                              <ol className="topic-test-preview-answers" type="A">
                                {getPreguntaAnswers(pregunta).map((respuesta, respuestaIndex) => (
                                  <li
                                    key={`${previewTestModal.test.titulo || "test"}-${index}-${respuestaIndex}`}
                                    className={pregunta?.correcta === respuestaIndex ? "is-correct" : ""}
                                  >
                                    {respuesta || `Respuesta ${respuestaIndex + 1}`}
                                    {pregunta?.correcta === respuestaIndex ? " · Correcta" : ""}
                                  </li>
                                ))}
                              </ol>
                            ) : null}
                            {pregunta?.explicacion ? (
                              <p className="topic-test-preview-explanation">
                                Explicación: {pregunta.explicacion}
                              </p>
                            ) : null}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="topic-test-preview-empty">Este test no tiene preguntas configuradas.</p>
                    )}
                  </>
                ) : (
                  <p className="topic-test-preview-empty">
                    No se encontraron los datos completos de este test en la vista previa.
                  </p>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TopicViewer;
